import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import PageContainer from "../pageContainer";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import { SectionTitle } from "../typography";
import { useLocalization } from "gatsby-theme-i18n";
import { Button, ButtonIconContainerSuffix, ButtonRightIcon } from "../button";
import { when } from "../../styles/theme";

export const fragment = graphql`
  fragment JoinUsContent on ContentfulPageSectionJoinUs {
    name
    title
    description {
      childMarkdownRemark {
        html
      }
    }
    image {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 450, height: 450)
    }
    formLink

    internal {
      type
    }
  }
`;

const translations = {
  en: {
    joinUs: "Join Us",
  },
  zh: {
    joinUs: "加入我們",
  },
};

const JoinUsSection = styled.section`
  background-color: ${(props) => props.theme.colors.red};
  padding: 8rem 0;
  width: 100%;
  overflow: hidden;

  ${when("md")} {
    padding: 15rem 0;
  }
`;

const ContentContainer = styled(PageContainer)`
  margin-top: 5rem;
  &:first-child {
    margin-top: 0;
  }

  ${when("md")} {
    display: flex;
    align-items: center;
    column-gap: ${(props) => props.theme.gutterWidth.md}rem;
    max-width: 99.6rem;
    justify-content: space-between;
  }

  ${when("lg")} {
    column-gap: ${(props) => props.theme.gutterWidth.lg}rem;
  }

  ${when("xl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }

  ${when("xxl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }
`;

const Title = styled(SectionTitle)`
  color: white;
  margin-top: 5rem;
  font-size: 2.8rem;
  line-height: 1.5;

  ${when("md")} {
    font-size: 4.8rem;
    margin-top: 0;
  }
`;

const Description = styled.div`
  margin-top: 0.5rem;
  text-align: center;
  display: flex;
  justify-content: center;
  max-width: 80%;

  ${when("md")} {
    margin-top: 3rem;
    text-align: left;
    padding-right: 4rem;
    max-width: unset;
  }
`;

const ImageContainer = styled.div`
  ${when("md")} {
    order: 2;
  }
`;
const ImageWrapper = styled.div`
  position: relative;
  max-width: 23rem;

  margin: 0 auto;

  ${when("md")} {
    max-width: 37rem;
  }
`;

const LineContainer = styled.div`
  position: absolute;
  width: 80%;
  height: 80%;
  bottom: -0.5rem;
  right: -0.5rem;
`;

const Image = styled(GatsbyImage)`
  overflow: hidden;
  border-radius: 50%;
  transform: translateZ(0);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  color: white;

  ${when("md")} {
    max-width: 45rem;
    align-items: flex-start;
  }
  
  ${when("lg")} {
    max-width: 55rem;
  }
`;

const JoinUsButton = styled(Button)`
  padding: 0.8rem 4rem;
  margin-top: 3rem;
  color: ${(props) => props.theme.colors.red};
  border: none;
`;

const JoinUs = ({ data }) => {
  const { image, title, description, formLink } = data;

  const { locale } = useLocalization();

  const translation = translations[locale];
  return (
    <JoinUsSection>
      <ContentContainer>
        <ImageContainer>
          <ImageWrapper>
            <LineContainer>
              <StaticImage
                src="../../images/background_pattern.png"
                alt=""
                width={450}
                height={450}
                placeholder={"none"}
              />
            </LineContainer>
            <Image image={image?.gatsbyImageData} alt={image?.description} />
          </ImageWrapper>
        </ImageContainer>
        <Content>
          <Title>{title}</Title>
          <Description
            dangerouslySetInnerHTML={{
              __html: description.childMarkdownRemark.html,
            }}
          />
          <JoinUsButton as="a" href={formLink} target="_blank">
            {translation["joinUs"]}{" "}
            <ButtonIconContainerSuffix>
              <ButtonRightIcon />
            </ButtonIconContainerSuffix>
          </JoinUsButton>
        </Content>
      </ContentContainer>
    </JoinUsSection>
  );
};
export const joinUsName = "ContentfulPageSectionJoinUs";

export default JoinUs;
